/**
 * Menu Helper Functions
 * Utility functions for handling hierarchical menu structure
 */

/**
 * Convert string to URL-friendly slug
 * @param {string} text - Text to convert to slug
 * @returns {string} URL-friendly slug
 */
export const slugify = (text) => {
  if (!text) return '';
  
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')        // Replace spaces with -
    .replace(/[^\w\-]+/g, '')    // Remove all non-word chars
    .replace(/\-\-+/g, '-')      // Replace multiple - with single -
    .replace(/^-+/, '')          // Trim - from start
    .replace(/-+$/, '');         // Trim - from end
};

/**
 * Build hierarchical menu structure from flat array
 * @param {Array} menuItems - Flat array of menu items
 * @param {number} parentId - Parent ID to filter by (default: 0)
 * @returns {Array} Hierarchical menu structure
 */
export const buildMenuHierarchy = (menuItems, parentId = 0) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return [];
  }

  const children = menuItems
    .filter(item => Number(item.parent_id) === parentId)
    .sort((a, b) => a.urutan - b.urutan);

  return children.map(item => ({
    ...item,
    children: buildMenuHierarchy(menuItems, item.id)
  }));
};

/**
 * Get all parent menus from flat array
 * @param {Array} menuItems - Flat array of menu items
 * @returns {Array} Parent menu items only
 */
export const getParentMenus = (menuItems) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return [];
  }

  return menuItems
    .filter(item => Number(item.parent_id) === 0)
    .sort((a, b) => a.urutan - b.urutan);
};

/**
 * Get submenu items by parent ID
 * @param {Array} menuItems - Flat array of menu items
 * @param {number} parentId - Parent menu ID
 * @returns {Array} Submenu items
 */
export const getSubmenuItems = (menuItems, parentId) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return [];
  }

  return menuItems
    .filter(item => Number(item.parent_id) === parentId)
    .sort((a, b) => a.urutan - b.urutan);
};

/**
 * Check if menu has children
 * @param {Array} menuItems - Flat array of menu items
 * @param {number} menuId - Menu ID to check
 * @returns {boolean} True if menu has children
 */
export const hasChildren = (menuItems, menuId) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return false;
  }

  return menuItems.some(item => Number(item.parent_id) === menuId);
};

/**
 * Get breadcrumb path for a menu item
 * @param {Array} menuItems - Flat array of menu items
 * @param {number} menuId - Menu ID
 * @returns {Array} Breadcrumb path
 */
export const getBreadcrumb = (menuItems, menuId) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return [];
  }

  const breadcrumb = [];
  let currentId = menuId;

  while (currentId !== 0) {
    const menu = menuItems.find(item => item.id === currentId);
    if (!menu) break;
    
    breadcrumb.unshift(menu);
    currentId = Number(menu.parent_id);
  }

  return breadcrumb;
};

/**
 * Flatten hierarchical menu structure
 * @param {Array} hierarchicalMenu - Hierarchical menu structure
 * @returns {Array} Flat menu array
 */
export const flattenMenu = (hierarchicalMenu) => {
  if (!hierarchicalMenu || !Array.isArray(hierarchicalMenu)) {
    return [];
  }

  const result = [];
  
  const flatten = (items) => {
    items.forEach(item => {
      const { children, ...menuItem } = item;
      result.push(menuItem);
      if (children && children.length > 0) {
        flatten(children);
      }
    });
  };
  
  flatten(hierarchicalMenu);
  return result;
};

/**
 * Count total submenus for a menu item
 * @param {Array} menuItems - Flat array of menu items
 * @param {number} menuId - Menu ID
 * @returns {number} Total submenu count
 */
export const countSubmenus = (menuItems, menuId) => {
  if (!menuItems || !Array.isArray(menuItems)) {
    return 0;
  }

  return menuItems.filter(item => Number(item.parent_id) === menuId).length;
};
